import {
  Badge,
  FormLabel,
  HStack,
  Input,
  Select,
  Td,
  Textarea,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { entityProfileModalColors } from "../../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfilePatient } from "../../../../patientProfileModal.types";
import type { AddressDraft, PatientAddress } from "../addresses.types";
import { formatEvvLocation, requiredIndicator } from "../addresses.utils";
import { AddressInput } from "./AddressFields";
import { AddressPhoneControl, PhoneNumberList } from "./AddressPhoneControls";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";

type AddressRowActions = {
  isDeleting: boolean;
  isSaving: boolean;
  onDeleteAddress: (address: PatientAddress) => void;
  onSaveAddress: (address: AddressDraft) => void;
};

export function ExistingAddressRow(props: {
  actions: AddressRowActions;
  address: AddressDraft;
  index: number;
  isEditing: boolean;
  patient: PatientProfilePatient;
  wasDraftSubmitted: (draft: AddressDraft) => boolean;
  onUpdateAddress: (index: number, address: AddressDraft) => void;
}) {
  const { address } = props;

  return (
    <Tr
      key={address.id}
      bg={address.isPrimary ? entityProfileModalColors.rowHighlightBg : undefined}
    >
      <Td>
        {props.isEditing && !address.isPrimary ? (
          <AddressInput
            address={address}
            isInvalid={props.wasDraftSubmitted(address) && address.address.trim() === ""}
            minW="300px"
            onChange={(value) => props.onUpdateAddress(props.index, { ...address, address: value })}
          />
        ) : (
          address.address
        )}
      </Td>
      <Td>
        {props.isEditing && !address.isPrimary ? (
          <Input
            aria-label="Address 2"
            placeholder="Unspecified"
            value={address.address2 ?? ""}
            onChange={(event) =>
              props.onUpdateAddress(props.index, {
                ...address,
                address2: event.target.value || null,
              })
            }
          />
        ) : (
          address.address2 ?? ""
        )}
      </Td>
      <Td>
        {props.isEditing ? (
          <Select
            aria-label="EVV Location"
            minW="140px"
            value={address.evvLocation}
            onChange={(event) =>
              props.onUpdateAddress(props.index, {
                ...address,
                evvLocation: event.target.value as PatientAddress["evvLocation"],
              })
            }
          >
            <option value="HOME">Home</option>
            <option value="COMMUNITY">Community</option>
          </Select>
        ) : (
          formatEvvLocation(address.evvLocation)
        )}
      </Td>
      <Td>
        {props.isEditing ? (
          <AddressPhoneControl
            patient={props.patient}
            value={address.phoneNumberIds}
            onChange={(phoneNumberIds) =>
              props.onUpdateAddress(props.index, { ...address, phoneNumberIds })
            }
          />
        ) : (
          <PhoneNumberList patient={props.patient} phoneNumberIds={address.phoneNumberIds} />
        )}
      </Td>
      <Td>
        {props.isEditing ? (
          <Textarea
            aria-label="Note"
            minW="180px"
            placeholder="Unspecified"
            rows={2}
            value={address.description ?? ""}
            onChange={(event) =>
              props.onUpdateAddress(props.index, {
                ...address,
                description: event.target.value || null,
              })
            }
          />
        ) : (
          address.description ?? ""
        )}
      </Td>
      <Td>
        {address.isPrimary ? (
          <Tooltip hasArrow label="Primary address text is managed from the summary address.">
            <Badge colorScheme="blue">Primary</Badge>
          </Tooltip>
        ) : null}
      </Td>
      {props.isEditing ? (
        <Td>
          <HStack>
            <EntityModalButton
              isIconOnly
              aria-label="Save address"
              iconClassName="fa fa-check"
              isLoading={props.actions.isSaving}
              kind="blue"
              size="xs"
              title="Save address"
              onClick={() => props.actions.onSaveAddress(address)}
            />
            {!address.isPrimary ? (
              <EntityModalButton
                iconClassName="fa fa-trash"
                isLoading={props.actions.isDeleting}
                kind="red"
                size="xs"
                variant="outline"
                onClick={() => props.actions.onDeleteAddress(address as PatientAddress)}
              >
                Delete
              </EntityModalButton>
            ) : null}
          </HStack>
        </Td>
      ) : null}
    </Tr>
  );
}

export function NewAddressRow(props: {
  address: AddressDraft;
  actions: Pick<AddressRowActions, "isSaving" | "onSaveAddress"> & { onCancel: () => void };
  patient: PatientProfilePatient;
  wasDraftSubmitted: (draft: AddressDraft) => boolean;
  onChange: (address: AddressDraft) => void;
}) {
  return (
    <Tr bg={entityProfileModalColors.rowEditingBg}>
      <Td>
        <FormLabel fontSize="xs" mb={1}>
          Address {requiredIndicator}
        </FormLabel>
        <AddressInput
          address={props.address}
          isInvalid={props.wasDraftSubmitted(props.address) && props.address.address.trim() === ""}
          minW="300px"
          onChange={(value) => props.onChange({ ...props.address, address: value })}
        />
      </Td>
      <Td>
        <FormLabel fontSize="xs" mb={1}>
          Address 2
        </FormLabel>
        <Input
          aria-label="Address 2"
          placeholder="Address 2"
          value={props.address.address2 ?? ""}
          onChange={(event) =>
            props.onChange({ ...props.address, address2: event.target.value || null })
          }
        />
      </Td>
      <Td>
        <FormLabel fontSize="xs" mb={1}>
          EVV Location {requiredIndicator}
        </FormLabel>
        <Select
          aria-label="EVV Location"
          minW="140px"
          value={props.address.evvLocation}
          onChange={(event) =>
            props.onChange({
              ...props.address,
              evvLocation: event.target.value as PatientAddress["evvLocation"],
            })
          }
        >
          <option value="HOME">Home</option>
          <option value="COMMUNITY">Community</option>
        </Select>
      </Td>
      <Td>
        <FormLabel fontSize="xs" mb={1}>
          Phone numbers
        </FormLabel>
        <AddressPhoneControl
          patient={props.patient}
          value={props.address.phoneNumberIds}
          onChange={(phoneNumberIds) => props.onChange({ ...props.address, phoneNumberIds })}
        />
      </Td>
      <Td>
        <FormLabel fontSize="xs" mb={1}>
          Note
        </FormLabel>
        <Textarea
          aria-label="Note"
          minW="180px"
          rows={2}
          value={props.address.description ?? ""}
          onChange={(event) =>
            props.onChange({ ...props.address, description: event.target.value || null })
          }
        />
      </Td>
      <Td />
      <Td>
        <HStack>
          <EntityModalButton
            isIconOnly
            aria-label="Save address"
            iconClassName="fa fa-check"
            isLoading={props.actions.isSaving}
            kind="blue"
            size="xs"
            title="Save address"
            onClick={() => props.actions.onSaveAddress(props.address)}
          />
          <EntityModalButton kind="white" size="xs" onClick={props.actions.onCancel}>
            Cancel
          </EntityModalButton>
        </HStack>
      </Td>
    </Tr>
  );
}
